import React, { useState, useEffect, useRef } from "react";
import { Button, Modal, Form, Badge } from "react-bootstrap";
import * as yup from "yup";
import { useUsers } from "../../context/reducers/usersContext";
import { editUser, getUser } from "../../context/fetch/fetchUsers";
import EditInput from "./editInput";
import { Loader } from "../../core/Loader";

const schema = yup.object().shape({
  name: yup.string().required({ key: "name", value: "Name is required" }),
  email: yup
    .string()
    .email()
    .required({ key: "email", value: "Email is required" }),
  company: yup
    .string()
    .required({ key: "company", value: "Company is required" }),
});

const EditModal = ({ id, onChangeToggle }) => {
  const [show, setShow] = useState(true);
  const [users, dispatch] = useUsers();
  const [roles, setRoles] = useState(users.users.user.roles || []);
  const [status, setStatus] = useState(users.users.user.status);
  const [value, setValue] = useState({ obj: {}, id: id });

  const handleClose = () => {
    onChangeToggle(false);
    setShow(false);
  };

  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const companyRef = useRef(null);
  const streetRef = useRef(null);
  const zipRef = useRef(null);
  const countryRef = useRef(null);
  const cityRef = useRef(null);
  const phoneRef = useRef(null);

  const [state, setState] = useState({
    errors: {
      name: "",
      email: "",
      company: "",
    },
  });

  const [edit, setEdit] = useState(false);

  useEffect(() => {
    getUser(id, dispatch);
  }, [id, dispatch]);

  useEffect(() => {
    if (users.users.user.roles) {
      setRoles(users.users.user.roles);
    }
  }, [users.users.user.id]);

  useEffect(() => {
    return () =>
      edit &&
      (setValue({ obj: {} }),
      setState({
        errors: { name: "", email: "", company: "" },
      }),
      handleClose());
  });

  const handleUpdateItem = (e) => {
    setValue({ ...value, obj: { ...value.obj, ...e.obj } });
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    schema
      .validate(
        {
          name: nameRef.current.value,
          email: emailRef.current.value,
          company: companyRef.current.value,
        },
        { abortEarly: false }
      )
      .then(async (valid) => {
        if (Object.keys(value.obj).length) {
          if (
            !(
              nameRef.current.value === users.users.user.name &&
              emailRef.current.value === users.users.user.email &&
              companyRef.current.value === users.users.user.company &&
              streetRef.current.value === users.users.user.street &&
              zipRef.current.value === users.users.user.zip &&
              cityRef.current.value === users.users.user.cityRef &&
              countryRef.current.value === users.users.user.country &&
              phoneRef.current.value === users.users.user.phone &&
              JSON.stringify(roles) === JSON.stringify(users.users.user.roles) &&
              status === users.users.user.status
            )
          ) {
            editUser(value, dispatch);
            setEdit(true);
          }
        } else {
          console.log("No field changed");
        }
      })
      .catch((err) => {
        const newState = {
          errors: {
            name: "",
            email: "",
            company: "",
          },
        };
        err.errors.forEach((err) => {
          newState.errors[err.key] = err.value;
        });
        setState({ ...state, ...newState });
      });
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-left">
          {users.users.userLoading ? (
            <Loader />
          ) : (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="formg-group mb-3">
                <EditInput
                  type="text"
                  name="email"
                  ref={emailRef}
                  placeholder="Email"
                  value={users.users.user.email}
                  updateValue={handleUpdateItem}
                  id="email"
                />
                {state.errors.email && (
                  <Form.Text className="text-danger">
                    {state.errors.email}
                  </Form.Text>
                )}
              </Form.Group>
              <Form.Group className="formg-group mb-3">
                <EditInput
                  type="text"
                  name="name"
                  ref={nameRef}
                  placeholder="Name"
                  value={users.users.user.name}
                  updateValue={handleUpdateItem}
                  id="name"
                />
                {state.errors.name && (
                  <Form.Text className="text-danger">
                    {state.errors.name}
                  </Form.Text>
                )}
              </Form.Group>
              <Form.Group className="formg-group mb-3">
                {/*<Form.Label>Roles</Form.Label>*/}
                <div className="d-flex gap-2 mb-2 flex-wrap">
                  {roles.map((role) => (
                    <Badge
                      key={role}
                      bg="primary"
                      style={{ cursor: "pointer", fontSize: "0.9rem", padding: "0.5rem 0.75rem" }}
                      onClick={() => {
                        const newRoles = roles.filter((r) => r !== role);
                        setRoles(newRoles);
                        setValue({
                          ...value,
                          obj: { ...value.obj, roles: newRoles },
                        });
                      }}
                    >
                      {role} ✕
                    </Badge>
                  ))}
                </div>
                <Form.Select
                  onChange={(e) => {
                    const role = e.target.value;
                    if (role && !roles.includes(role)) {
                      const newRoles = [...roles, role];
                      setRoles(newRoles);
                      setValue({
                        ...value,
                        obj: { ...value.obj, roles: newRoles },
                      });
                    }
                    e.target.value = "";
                  }}
                  defaultValue=""
                >
                  <option value="">Select role to add...</option>
                  {["ADMIN", "PUBLISHER", "CUSTOMER"]
                    .filter((role) => !roles.includes(role))
                    .map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="formg-group mb-3">
                <select
                  defaultValue={users.users.user.status}
                  disabled={roles.includes("ADMIN")}
                  onChange={(e) => {
                    setStatus(e.target.value);
                    setValue({
                      ...value,
                      obj: { ...value.obj, status: e.target.value },
                    });
                  }}
                  className="form-control"
                  id="exampleFormControlSelect1"
                >
                  <option name="status" value="INACTIVE">
                    Inactive
                  </option>
                  <option name="status" value="ACTIVE">
                    Active
                  </option>
                </select>
              </Form.Group>
              <Form.Group className="formg-group mb-3">
                <EditInput
                  type="text"
                  name="company"
                  ref={companyRef}
                  placeholder="Company"
                  value={users.users.user.company}
                  updateValue={handleUpdateItem}
                  id="company"
                />
                {state.errors.company && (
                  <Form.Text className="text-danger">
                    {state.errors.company}
                  </Form.Text>
                )}
              </Form.Group>
              <Form.Group className="formg-group mb-3">
                <EditInput
                  type="text"
                  name="street"
                  ref={streetRef}
                  placeholder="Street"
                  id="street"
                  updateValue={handleUpdateItem}
                  value={users.users.user?.street}
                />
              </Form.Group>

              <Form.Group className="formg-group mb-3">
                <EditInput
                  type="text"
                  name="city"
                  ref={cityRef}
                  placeholder="City"
                  value={users.users.user?.city}
                  updateValue={handleUpdateItem}
                  id="city"
                />
              </Form.Group>
              <Form.Group className="formg-group mb-3">
                <EditInput
                  type="text"
                  name="country"
                  ref={countryRef}
                  placeholder="Country"
                  value={users.users.user?.country}
                  updateValue={handleUpdateItem}
                  id="country"
                />
              </Form.Group>
              <Form.Group className="formg-group mb-3">
                <EditInput
                  type="text"
                  name="zip"
                  ref={zipRef}
                  placeholder="Zip"
                  value={users.users.user?.zip}
                  updateValue={handleUpdateItem}
                  id="zip"
                />
              </Form.Group>
              <Form.Group className="formg-group">
                <EditInput
                  type="text"
                  name="phone"
                  ref={phoneRef}
                  placeholder="Phone"
                  value={users.users.user?.phone}
                  updateValue={handleUpdateItem}
                  id="phone"
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={users.users.userEditLoading}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={users.users.userEditLoading}
          >
            {users.users.userEditLoading ? "...Loading" : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditModal;
